import {
  countUnitsOnBoard,
  totalHpOnBoard,
} from "@cloudnest/augmented-war-engine";
import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { Board, combatGlyph } from "./components/Board";
import { AUGMENTED_WAR_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import {
  type AugmentedWarReplaySnapshot,
  buildAugmentedWarReplaySnapshots,
} from "./replay-runner";

interface ReplayScreenProps {
  readonly scenario: string;
}

function clampStepIndex(
  index: number,
  snapshots: readonly AugmentedWarReplaySnapshot[],
): number {
  if (snapshots.length === 0) return 0;
  return Math.min(Math.max(0, index), snapshots.length - 1);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepIndexFromSearch(
  snapshots: readonly AugmentedWarReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;
  const params = new URLSearchParams(window.location.search);
  const seq = parseInteger(params.get("seq"));
  if (seq !== null) {
    const index = snapshots.findIndex((s) => s.event.sequenceNumber === seq);
    if (index >= 0) return index;
  }
  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStepIndex(step, snapshots);
  return 0;
}

function replaceStepQuery(
  index: number,
  snapshots: readonly AugmentedWarReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;
  const url = new URL(window.location.href);
  url.searchParams.set("step", String(index));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

const who = (p: string): string => (p === "p1" ? "P1" : "P2");

function actionCaption(snapshot: AugmentedWarReplaySnapshot): string {
  const a = snapshot.action;
  if (!a) return "게임 시작 (init)";
  if (a.type === "Move") return `${who(a.playerId)}  Move  ${a.from} → ${a.to}`;
  if (a.type === "Deploy") return `${who(a.playerId)}  Deploy → ${a.to}`;
  if (a.type === "EndTurn") return `${who(a.playerId)}  ends turn`;
  if (a.type === "Attack")
    return `${who(a.playerId)}  Attack  ${a.from} → ${a.target}  (${combatGlyph[a.attackerElement]} vs ${combatGlyph[a.defenderElement]})`;
  // Fixtures use the atomic god-view Attack; live-only Declare/Resolve are unused here.
  return `${who(a.playerId)}  ${a.type}`;
}

function combatLine(snapshot: AugmentedWarReplaySnapshot): string {
  if (snapshot.action?.type !== "Attack") return "";
  const c = snapshot.state.lastCombat;
  if (!c) return "";
  const outcome =
    c.outcome === "attackerWins"
      ? "attacker wins"
      : c.outcome === "defenderWins"
        ? "defender wins"
        : "standoff";
  let s = outcome;
  if (c.bastionNegated) s += ` · Bastion (${who(c.bastionNegated)})`;
  else if (c.removedCell) s += ` · ${c.removedCell} removed`;
  else if (c.damagedCell) s += ` · ${c.damagedCell} −1 HP`;
  if (c.advancedTo) s += ` · advanced → ${c.advancedTo}`;
  return s;
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fixture = AUGMENTED_WAR_REPLAY_FIXTURES[scenario] ?? null;
  const snapshots = useMemo(
    () => (fixture ? buildAugmentedWarReplaySnapshots(fixture.events) : []),
    [fixture],
  );
  const [stepIndex, setStepIndex] = useState(() =>
    resolveStepIndexFromSearch(snapshots),
  );
  const safeStepIndex = clampStepIndex(stepIndex, snapshots);
  const current = snapshots[safeStepIndex] ?? snapshots[0] ?? null;

  useEffect(() => {
    setStepIndex(resolveStepIndexFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(index: number): void {
    const nextIndex = clampStepIndex(index, snapshots);
    setStepIndex(nextIndex);
    replaceStepQuery(nextIndex, snapshots);
  }

  if (!fixture || !current) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-primary p-6 text-text-primary">
        <section className="max-w-sm rounded-md border border-border-primary bg-surface-secondary p-6 text-center">
          <h1 className="mb-2 font-semibold text-lg">
            리플레이를 찾을 수 없습니다
          </h1>
          <p className="mb-4 text-sm text-text-secondary">{scenario}</p>
          <button
            type="button"
            className="rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white"
            onClick={() => navigateTo("/")}
          >
            닫기
          </button>
        </section>
      </div>
    );
  }

  const state = current.state;
  const maxIndex = snapshots.length - 1;
  const isGameOver = state.phase === "GAME_END";
  const result = state.result;
  const toMove = isGameOver
    ? "게임 종료"
    : `${who(state.currentPlayerId)} 차례`;

  return (
    <div
      className="min-h-screen bg-surface-primary text-text-primary"
      data-testid="augmented-war-replay-screen"
    >
      <div
        className="sticky top-0 z-20 border-border-primary border-b bg-surface-primary/95 px-4 py-3 backdrop-blur"
        data-testid="augmented-war-replay-toolbar"
      >
        <div className="mx-auto flex max-w-5xl flex-col gap-3 md:flex-row md:items-center">
          <div className="min-w-0 flex-1">
            <p
              className="truncate font-semibold text-sm"
              data-testid="augmented-war-replay-event-summary"
            >
              {`#${current.event.sequenceNumber} ${current.event.eventType}`} ·{" "}
              {who(current.event.userId)}
            </p>
            <p
              className="text-text-secondary text-xs"
              data-testid="augmented-war-replay-phase"
            >
              턴 {state.turn} · {toMove} · AP {state.apRemaining}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm disabled:opacity-40"
              data-testid="augmented-war-replay-prev"
              disabled={safeStepIndex === 0}
              onClick={() => setReplayStep(safeStepIndex - 1)}
            >
              Prev
            </button>
            <input
              type="range"
              min={0}
              max={maxIndex}
              value={safeStepIndex}
              className="w-36 md:w-56"
              data-testid="augmented-war-replay-step-slider"
              onChange={(event) => setReplayStep(Number(event.target.value))}
            />
            <button
              type="button"
              className="rounded-md border border-border-primary px-3 py-2 font-semibold text-sm disabled:opacity-40"
              data-testid="augmented-war-replay-next"
              disabled={safeStepIndex === maxIndex}
              onClick={() => setReplayStep(safeStepIndex + 1)}
            >
              Next
            </button>
            <span
              className="w-16 text-right font-semibold text-sm text-text-secondary"
              data-testid="augmented-war-replay-step-count"
            >
              {safeStepIndex + 1}/{snapshots.length}
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto flex max-w-5xl flex-col gap-4 p-4 md:flex-row">
        <div className="min-w-0 flex-1 rounded-md border border-border-primary bg-surface-secondary p-2">
          <Board state={state} />
        </div>
        <aside className="flex flex-col gap-3 md:w-64">
          {(["p1", "p2"] as const).map((p) => (
            <div
              key={p}
              className="rounded-md border border-border-primary bg-surface-secondary p-3 text-sm"
            >
              <div className="font-semibold">
                {who(p)} · {state.players[p].faction}
              </div>
              <div className="text-text-secondary text-xs">
                reserve {state.players[p].reserve} · on board{" "}
                {countUnitsOnBoard(state.board, p)} · HP{" "}
                {totalHpOnBoard(state.board, p)}
              </div>
            </div>
          ))}
          <div className="rounded-md border border-border-primary bg-surface-secondary p-3 text-sm">
            <div className="font-semibold">{actionCaption(current)}</div>
            {combatLine(current) ? (
              <div className="mt-1 text-text-secondary text-xs">
                {combatLine(current)}
              </div>
            ) : null}
          </div>
          {isGameOver ? (
            <div
              className="rounded-md border border-border-primary bg-surface-secondary p-3 text-center font-semibold text-sm"
              data-testid="augmented-war-replay-game-over"
            >
              {result
                ? `${result.kind}${result.winner ? ` · ${who(result.winner)} 승` : " · 무승부"}`
                : "게임 종료"}
            </div>
          ) : null}
        </aside>
      </div>
    </div>
  );
}
